import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  Fragment
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { Helmet } from 'react-helmet'

import { StepHandlerContext } from '../../contexts/stepHandler.context'
import { NextButtonAvailabilityContext } from '../../contexts/nextButtonAvailability.context'
import {
  setSubscriptionBundle,
  setSubscriptionVariants,
  setSubscriptionMix,
  setSubscriptionStep,
  setSubscriptionSizeSubSteps,
  goToSize
} from '../../store/subscription/subscription.action'
import {
  selectSubscriptionStep,
  selectSubscriptionSizeSubSteps
} from '../../store/subscription/subscription.selector'
import {
  SIZE_STEP,
  DIAPERS_BUNDLE,
  MIX_BUNDLE,
  BUNDLES_DATA
} from '../../utils/bab/bab.utils'

import Title from '../../components/title/title.component'
import Text from '../../components/text/text.component'
import BundleGrid from '../../components/bundle-grid/bundle-grid.component'
import PopinWrapper from '../../components/popin-wrapper/popin-wrapper.component'
import DiapersPantsModal from '../../../shared/components/atoms/DiapersPantsModalContent'

import './bundle.styles.scss'

const Bundle = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const [localBundle, setLocalBundle] = useState(DIAPERS_BUNDLE.id)
  const localBundleRef = useRef(localBundle)

  const { setPreviousStep, setNextStep } = useContext(StepHandlerContext)
  const { nextButtonAvailability, setNextButtonAvailability } = useContext(
    NextButtonAvailabilityContext
  )

  const subscriptionStep = useSelector(selectSubscriptionStep)
  const sizeSubSteps = useSelector(selectSubscriptionSizeSubSteps)

  const bundlesData = BUNDLES_DATA.filter((bundle) => {
    return bundle.id !== 'diapers'
  })

  // Gestion du cycle de vie composant
  useEffect(() => {
    const previousAvailability = nextButtonAvailability
    setPreviousStep(() => handlePreviousStep)
    setNextStep(() => handleNextStep)
    setNextButtonAvailability(false)

    return () => {
      setLocalBundle({})
      setPreviousStep(() => {})
      setNextStep(() => {})
      setNextButtonAvailability(previousAvailability)
    }
  }, [subscriptionStep, sizeSubSteps])

  // Gestion du clic sur le bouton précédent

  // Gestion de la disponibilité du bouton suivant
  useEffect(() => {
    const bundleType = localBundle
    localBundleRef.current = bundleType

    if (bundleType !== DIAPERS_BUNDLE.id) {
      setNextButtonAvailability(true)
    } else {
      setNextButtonAvailability(false)
    }
  }, [localBundle])

  const handlePreviousStep = () => {
    dispatch(setSubscriptionBundle(DIAPERS_BUNDLE.id))

    dispatch(goToSize())
    history.replace(SIZE_STEP.path)
  }

  // Gestion du clic sur le bouton suivant
  const handleNextStep = () => {
    const updatedSubscriptionBundle = localBundleRef.current
    dispatch(setSubscriptionBundle(updatedSubscriptionBundle))

    dispatch(setSubscriptionVariants({}))

    if (updatedSubscriptionBundle === MIX_BUNDLE.id) {
      dispatch(setSubscriptionMix(false))
      dispatch(setSubscriptionSizeSubSteps(1, 0))
    }

    dispatch(setSubscriptionStep(1))
    history.replace(SIZE_STEP.path)

    dispatch(setSubscriptionMix(false))
  }

  // Gestion du changement de type d'abonnement (état local)
  const handleBundleChange = (event) => {
    const targetElement = event.target
    const bundle = targetElement.closest('.bundle-selector')
    const readMore = targetElement.closest('.bundle-selector-text')

    if (!bundle || readMore) return false

    const bundleType = bundle.dataset.bundleType

    setLocalBundle(bundleType)
  }

  // Gestion de l'ouverture des pop-ups
  const handlePopIns = (event) => {
    const targetElement = event.target
    const cleanDiaper = targetElement.closest('.bundle-text strong')
    const readMore = targetElement.closest('.bundle-selector-text')
    const bundleQuestion = targetElement.closest('.bundle-question')

    if (!bundleQuestion && !readMore && !cleanDiaper) return false

    document
      .querySelector('#bundle-popin .popin-content')
      .childNodes.forEach((popin) => {
        popin.style.display = 'none'
      })

    if (cleanDiaper) {
      document.querySelector('.diapers-popin').style.display = null
    } else if (readMore) {
      const bundleType = readMore.dataset.bundle

      bundleType === 'mix'
        ? (document.querySelector('.mix-popin').style.display = null)
        : (document.querySelector('.pants-popin').style.display = null)
    } else if (bundleQuestion) {
      document.querySelector('.reassurance-popin').style.display = null
    }

    document.querySelector('#bundle-popin').setAttribute('data-open', '')
  }

  return (
    <Fragment>
      <Helmet>
        <title>{t('updatedBAB:bundle.metaData.title')}</title>
        <meta
          name='description'
          content={t('updatedBAB:bundle.metaData.description')}
        />
      </Helmet>
      <Title
        content={t('updatedBAB:bundle.title', { returnObjects: true })}
        style={1}
      />
      <Text
        content={<Trans i18nKey='updatedBAB:bundle.text' />}
        classNames='bundle-text'
        margins={[16, 0, 16, 0]}
        clickHandler={handlePopIns}
      />
      <BundleGrid
        currentBundle={localBundle}
        bundlesData={bundlesData}
        margins={[32, 0, 32, 0]}
        clickHandler={handleBundleChange}
        popUpClickHandler={handlePopIns}
      />
      <Text
        content={t('updatedBAB:bundle.question')}
        classNames='bundle-question'
        margins={[32, 0, 32, 0]}
        clickHandler={handlePopIns}
      />
      <PopinWrapper id={'bundle-popin'}>
        <div className='diapers-popin'>
          <DiapersPantsModal
            product={t(`productInfos:diapers`, {
              returnObjects: true
            })}
            type={'diapers'}
          />
        </div>
        <div className='pants-popin'>
          <DiapersPantsModal
            product={t(`productInfos:pants`, {
              returnObjects: true
            })}
            type={'pants'}
          />
        </div>
        <div className='mix-popin'>
          <DiapersPantsModal
            product={t('productInfos:mix', {
              returnObjects: true
            })}
            type={'mix'}
          />
        </div>
        <div className='reassurance-popin'>
          <h1 className='reassurance-popin-title'>
            {t('reassuranceModal:title')}
          </h1>
          <img
            className='reassurance-popin-image'
            src='https://cdn.shopify.com/s/files/1/1956/4693/files/couches_et_cc.svg?v=1623050559'
          />
          <p className='reassurance-popin-text'>
            {<Trans i18nKey='reassuranceModal:text' />}
          </p>
        </div>
      </PopinWrapper>
    </Fragment>
  )
}

export default Bundle
